/**
 * Registration page component.
 *
 * Supports registration profiles via ?profile= query parameter:
 *   - profile=individual — email, name, password only (no company)
 *   - profile=business   — current flow with company create/join (default)
 *   - profile=developer  — individual account with developer role
 *
 * Landing page routes (/register/personal, /register/business, /register/developer)
 * redirect to /register?profile=<profile>.
 *
 * Source app tracking via ?source_app= or inferred from return_to domain.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { RegistrationFlow, UiNodeInputAttributes } from '@ory/client';
import { kratos, getFlowId, getReturnTo, redirectTo, handleKratosError } from '../lib/kratos';
import { useAuth } from '../context/AuthContext';
import FlowMessages from '../components/FlowMessages';
import LoadingSpinner from '../components/LoadingSpinner';
import { PRODUCT_NAME, LOGO_MARK_LIGHT, LOGO_MARK_DARK, LOGO_ALT } from '../brand';
import { config } from '../config';

type RegistrationProfile = 'individual' | 'business' | 'developer';

const BASE_DOMAIN = config.baseDomain;
const PASSPORT_HOST = `passport.${BASE_DOMAIN}`;

function getSourceApp(search: string, returnTo: string): string | undefined {
  const params = new URLSearchParams(search);
  const explicit = params.get('source_app');
  if (explicit) return explicit;

  // Infer from return_to domain
  try {
    const url = new URL(returnTo);
    const host = url.hostname;
    // Extract subdomain from *.{BASE_DOMAIN}
    if (host.endsWith(`.${BASE_DOMAIN}`) && host !== PASSPORT_HOST) {
      return host.replace(`.${BASE_DOMAIN}`, '');
    }
    // For external domains, use the full hostname
    if (host !== PASSPORT_HOST && host !== 'localhost') {
      return host;
    }
  } catch {
    // Not a valid URL
  }
  return 'passport';
}

export default function Register() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [flow, setFlow] = useState<RegistrationFlow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyMode, setCompanyMode] = useState<'create' | 'join'>('create');

  const returnTo = getReturnTo(location.search);

  // Determine initial registration profile from URL, allow toggling
  const params = new URLSearchParams(location.search);
  const profileParam = params.get('profile');
  const initialProfile: RegistrationProfile =
    profileParam === 'individual' ? 'individual'
    : profileParam === 'developer' ? 'developer'
    : 'business';
  const [profile, setProfile] = useState<RegistrationProfile>(initialProfile);
  const sourceApp = getSourceApp(location.search, returnTo);

  const initializeFlow = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const flowId = getFlowId(location.search);

      if (flowId) {
        const { data } = await kratos.getRegistrationFlow({ id: flowId });
        setFlow(data);
      } else {
        // For developer profile, embed registration_profile in return_to
        // so the Gateway webhook can detect it and assign the developer role
        let flowReturnTo = returnTo;
        if (profile === 'developer') {
          try {
            const url = new URL(flowReturnTo, window.location.origin);
            url.searchParams.set('registration_profile', 'developer');
            flowReturnTo = url.toString();
          } catch {
            // If returnTo isn't a valid URL, append as query string
            const sep = flowReturnTo.includes('?') ? '&' : '?';
            flowReturnTo = `${flowReturnTo}${sep}registration_profile=developer`;
          }
        }
        const { data } = await kratos.createBrowserRegistrationFlow({
          returnTo: flowReturnTo,
        });
        // Preserve profile and source_app in URL
        const newParams = new URLSearchParams();
        newParams.set('flow', data.id);
        if (profile !== 'business') newParams.set('profile', profile);
        if (sourceApp) newParams.set('source_app', sourceApp);
        // Preserve return_to
        const rawReturnTo = new URLSearchParams(location.search).get('return_to');
        if (rawReturnTo) newParams.set('return_to', rawReturnTo);

        navigate(`/register?${newParams.toString()}`, { replace: true });
        setFlow(data);
      }
    } catch (err) {
      setError(handleKratosError(err));
    } finally {
      setIsLoading(false);
    }
  }, [location.search, navigate, returnTo, profile, sourceApp]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      redirectTo(returnTo, navigate);
      return;
    }

    initializeFlow();
  }, [initializeFlow, isAuthenticated, authLoading, navigate, returnTo]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(event.currentTarget);

    // Get csrf_token from flow
    const csrfNode = flow?.ui.nodes.find(
      (n) => (n.attributes as { name?: string }).name === 'csrf_token'
    );
    const csrfToken = csrfNode ? (csrfNode.attributes as { value?: string }).value : '';

    // Build traits based on profile
    // Developers are individual accounts; account_type maps to identity schema enum
    const accountType = profile === 'developer' ? 'individual' : profile;
    const traits: Record<string, unknown> = {
      email: formData.get('email') as string,
      name: {
        first: formData.get('first_name') as string,
        last: formData.get('last_name') as string,
      },
      account_type: accountType,
    };

    // source_app is NOT stored as a trait — it's derived from return_to
    // by the Gateway webhook and written as a Keto tuple

    // Add company fields only for business profile
    if (profile === 'business') {
      if (companyMode === 'create') {
        traits.company_name = formData.get('company_name') as string;
      } else {
        traits.company_code = formData.get('company_code') as string;
      }
    }

    const body = {
      method: 'password',
      csrf_token: csrfToken,
      password: formData.get('password') as string,
      traits,
    };

    try {
      await kratos.updateRegistrationFlow({
        flow: flow!.id,
        updateRegistrationFlowBody: body as unknown as Parameters<typeof kratos.updateRegistrationFlow>[0]['updateRegistrationFlowBody'],
      });

      // Registration successful — offer passkey setup if browser supports WebAuthn
      if (typeof window !== 'undefined' && window.PublicKeyCredential) {
        const setupUrl = `/settings?tab=security&setup_passkey=1&return_to=${encodeURIComponent(returnTo)}`;
        window.location.href = setupUrl;
      } else {
        window.location.href = returnTo;
      }
    } catch (err) {
      const errorMessage = handleKratosError(err);
      if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        initializeFlow();
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (error && !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Error</h1>
          <p className="text-content-secondary mb-4">{error}</p>
          <button onClick={initializeFlow} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-8">
          <img
            src={LOGO_MARK_LIGHT}
            alt={LOGO_ALT}
            className="h-16 w-16 mx-auto mb-4 dark:hidden"
          />
          <img
            src={LOGO_MARK_DARK}
            alt={LOGO_ALT}
            className="h-16 w-16 mx-auto mb-4 hidden dark:block"
          />
          <h1 className="text-2xl font-bold text-content-primary">Create Account</h1>
          <p className="text-content-secondary mt-1">
            {profile === 'individual'
              ? 'Create your personal account'
              : profile === 'developer'
              ? 'Create your developer account'
              : `Get started with ${PRODUCT_NAME}`}
          </p>
        </div>

        {/* Profile toggle */}
        <div className="flex rounded-lg border border-border overflow-hidden mb-6">
          <button
            type="button"
            onClick={() => setProfile('individual')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              profile === 'individual'
                ? 'bg-orbital-600 text-white'
                : 'bg-surface-primary text-content-primary hover:bg-surface-secondary'
            }`}
          >
            Personal
          </button>
          <button
            type="button"
            onClick={() => setProfile('business')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              profile === 'business'
                ? 'bg-orbital-600 text-white'
                : 'bg-surface-primary text-content-primary hover:bg-surface-secondary'
            }`}
          >
            Company
          </button>
          <button
            type="button"
            onClick={() => setProfile('developer')}
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
              profile === 'developer'
                ? 'bg-orbital-600 text-white'
                : 'bg-surface-primary text-content-primary hover:bg-surface-secondary'
            }`}
          >
            Developer
          </button>
        </div>

        <FlowMessages messages={flow?.ui.messages} />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div className="space-y-1">
            <label htmlFor="email" className="block text-sm font-medium text-content-primary">
              Email <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              disabled={isSubmitting}
              autoComplete="email"
              placeholder={profile === 'business' ? 'you@company.com' : 'you@example.com'}
              className="input-field"
            />
          </div>

          {/* Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="first_name" className="block text-sm font-medium text-content-primary">
                First Name <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                required
                disabled={isSubmitting}
                autoComplete="given-name"
                placeholder="John"
                className="input-field"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="last_name" className="block text-sm font-medium text-content-primary">
                Last Name <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                required
                disabled={isSubmitting}
                autoComplete="family-name"
                placeholder="Doe"
                className="input-field"
              />
            </div>
          </div>

          {/* Company section — only for business profile */}
          {profile === 'business' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-content-primary">
                Company
              </label>
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCompanyMode('create')}
                  className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                    companyMode === 'create'
                      ? 'bg-orbital-600 text-white'
                      : 'bg-surface-primary text-content-primary hover:bg-surface-secondary'
                  }`}
                >
                  Create New
                </button>
                <button
                  type="button"
                  onClick={() => setCompanyMode('join')}
                  className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                    companyMode === 'join'
                      ? 'bg-orbital-600 text-white'
                      : 'bg-surface-primary text-content-primary hover:bg-surface-secondary'
                  }`}
                >
                  Join Existing
                </button>
              </div>

              {companyMode === 'create' ? (
                <div className="space-y-1">
                  <label htmlFor="company_name" className="block text-sm font-medium text-content-primary">
                    Company Name <span className="text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="company_name"
                    name="company_name"
                    type="text"
                    required={companyMode === 'create'}
                    disabled={isSubmitting}
                    placeholder="Acme Inc."
                    className="input-field"
                  />
                  <p className="text-xs text-content-tertiary">
                    You'll be the owner of this company
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <label htmlFor="company_code" className="block text-sm font-medium text-content-primary">
                    Company Code <span className="text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="company_code"
                    name="company_code"
                    type="text"
                    required={companyMode === 'join'}
                    disabled={isSubmitting}
                    placeholder="ABC123"
                    className="input-field uppercase"
                  />
                  <p className="text-xs text-content-tertiary">
                    Get this code from your company admin
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Password */}
          <div className="space-y-1">
            <label htmlFor="password" className="block text-sm font-medium text-content-primary">
              Password <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              placeholder="••••••••••••"
              minLength={12}
              className="input-field"
            />
            <p className="text-xs text-content-tertiary">
              Minimum 12 characters
            </p>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isSubmitting ? <LoadingSpinner size="sm" /> : 'Create Account'}
          </button>
        </form>

        {/* OIDC / SSO buttons */}
        {(() => {
          const oidcNodes = flow?.ui.nodes.filter((n) => n.group === 'oidc') || [];
          const providers = oidcNodes.filter(
            (n) => (n.attributes as UiNodeInputAttributes).name === 'provider'
          );
          if (providers.length === 0) return null;

          const providerLabel = (id: string): string => {
            const labels: Record<string, string> = {
              microsoft: 'Microsoft', google: 'Google', github: 'GitHub',
              apple: 'Apple', okta: 'Okta', slack: 'Slack',
            };
            return labels[id] || id.charAt(0).toUpperCase() + id.slice(1);
          };

          const handleOidcRegister = (providerId: string) => {
            if (!flow) return;
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = flow.ui.action;
            form.style.display = 'none';
            const addField = (name: string, value: string) => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = name;
              input.value = value;
              form.appendChild(input);
            };
            for (const node of flow.ui.nodes) {
              if (node.group === 'default' && (node.attributes as { type?: string }).type === 'hidden') {
                const a = node.attributes as UiNodeInputAttributes;
                addField(a.name, a.value as string);
              }
            }
            addField('provider', providerId);
            addField('method', 'oidc');
            document.body.appendChild(form);
            form.submit();
          };

          return (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                <div className="relative flex justify-center text-xs"><span className="px-2 bg-surface-800 text-content-muted">or continue with</span></div>
              </div>
              <div className="space-y-2">
                {providers.map((node) => {
                  const attrs = node.attributes as UiNodeInputAttributes;
                  const id = attrs.value as string;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleOidcRegister(id)}
                      disabled={isSubmitting}
                      className="btn-secondary w-full flex items-center justify-center space-x-2"
                    >
                      <span>Sign up with {providerLabel(id)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          );
        })()}

        <div className="mt-6 text-center">
          <p className="text-content-secondary">
            Already have an account?{' '}
            <Link
              to={`/login?return_to=${encodeURIComponent(returnTo)}`}
              className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
